import React, { Fragment, useEffect, useState, lazy, Suspense, useRef } from "react";
import { useSelector } from "react-redux";
import { currentUser } from "store/ducks/user";
import { RootState } from "store/store";
import Header from "component/Header";
import { Logout } from "component/Logout";
import { SignIn } from "component/SignIn";
import { IsUserLoggedIn } from "pages/map/utils/RightSideMapUtils";
import { Bit } from "types/types";
import { GiftBitPopup } from "pages/home/components/GiftBitPopup";
import Loader from "component/Loader";
import { useNavigate } from "react-router";

const SidebarPage = lazy(() => import("pages/sidebar/components/Sidebar"));

interface MainLayoutProps {
    children: React.ReactNode;
    infoRouteName?: string;
    hideBanner?: boolean;
    setHideBanner?: (hide: boolean) => void;
    // Some pages might need access to these states
    onStateUpdate?: (state: any) => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
    children,
    infoRouteName = "",
    hideBanner: externalHideBanner,
    setHideBanner: externalSetHideBanner,
}) => {
    const user = useSelector(currentUser);
    const selectedCountry = useSelector(
        (state: RootState) => state.selectedCountry.country
    );
    const [logedIn, setLogedIn] = useState<boolean>(false);
    const [showSignupPopUp, setShowSignupPopUp] = useState<boolean>(false);
    const [isSidebarOpen, setSidebarOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const previousCountryIdRef = useRef<number | null>(null);

    // Internal state if not provided externally (like on Sitemap)
    const [internalHideBanner, setInternalHideBanner] = useState<boolean>(true);

    const hideBanner = externalHideBanner !== undefined ? externalHideBanner : internalHideBanner;
    const setHideBanner = externalSetHideBanner || setInternalHideBanner;

    const [logoutUser, setLogoutUser] = useState<boolean>(false);
    const [giftBitPopup, setGiftBitPopup] = useState<boolean>(false);
    const [loggedUserPurchasedBitInfo, setLoggedUserPurchasedBitInfo] = useState<
        null | Bit[]
    >(null);
    const [profileInfo, setProfileInfo] = useState<null | {
        ownBit: boolean;
        bitIndex: number;
    }>(null);

    const toggleSidebar = (openSidebar: boolean | any = false) => {
        if (openSidebar === true) {
            setSidebarOpen(true);
        } else {
            navigate("/");
            setSidebarOpen((prev) => !prev);
        }
    };

    useEffect(() => {
        const checkAuth = async () => {
            (await IsUserLoggedIn(user)) ? setLogedIn(true) : setLogedIn(false);
        };
        checkAuth();

        // Clean up scrolled class on mount/unmount to ensure fresh state
        document.body.classList.remove("scrolled");

        return () => {
            document.body.classList.remove("scrolled");
        };
    }, [user]);

    useEffect(() => {
        if (infoRouteName) {
            setSidebarOpen(true);
        }
    }, [infoRouteName]);

    // Handle country change: navigate to home and show banner
    useEffect(() => {
        // Only run when country actually changes (not on initial mount)
        if (
            selectedCountry?.id &&
            previousCountryIdRef.current !== null &&
            previousCountryIdRef.current !== selectedCountry.id
        ) {
            navigate("/");
            const bannerSection = document.getElementById("banner-section");
            if (!bannerSection) return;
            // Scroll first
            window.scrollTo({
                top: bannerSection.offsetTop,
                behavior: "smooth",
            });

            // Wait ~500ms (adjust to match smooth scroll speed) before showing banner
            setTimeout(() => {
                setHideBanner(false);
            }, 500);
        }
        // Update the ref to track current country
        if (selectedCountry?.id) {
            previousCountryIdRef.current = selectedCountry.id;
        }
    }, [selectedCountry?.id, navigate, setHideBanner]);

    
    return (
        <div className="relative min-h-screen">
            <Header
                onNavButtonClick={toggleSidebar}
                setHideBanner={setHideBanner}
                hideBanner={hideBanner}
                loggedIn={logedIn}
                setLogoutUser={setLogoutUser}
                setLogedIn={setLogedIn}
                loggedUserPurchasedBitInfo={loggedUserPurchasedBitInfo}
                setProfileInfo={setProfileInfo}
                setGiftBitPopup={setGiftBitPopup}
            />

            {isSidebarOpen && (
                <Fragment>
                    <div
                        className="fixed inset-0 z-[998] bg-black/50 backdrop-blur-sm fixed-body"
                        onClick={() => toggleSidebar()}
                    />
                    <Suspense fallback={<Loader />}>
                        <SidebarPage
                            isOpen={isSidebarOpen}
                            toggleSidebar={toggleSidebar}
                            infoRouteName={infoRouteName}
                        />
                    </Suspense>
                </Fragment>
            )}

            {/* Main Content */}
            <div className="layout-content">
                {React.Children.map(children, child => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child as React.ReactElement<any>, {
                            logedIn,
                            setShowSignupPopUp,
                            setLoggedUserPurchasedBitInfo,
                            loggedUserPurchasedBitInfo,
                            profileInfo,
                            setProfileInfo,
                            setGiftBitPopup,
                            isSidebarOpen,
                            hideBanner,
                            setHideBanner
                        });
                    }
                    return child;
                })}
            </div>

            {/* Popups */}
            {showSignupPopUp && <SignIn setShowSignupPopUp={setShowSignupPopUp} />}

            {giftBitPopup && (
                <GiftBitPopup
                    setGiftBitPopup={setGiftBitPopup}
                    loggedUserPurchasedBitInfo={loggedUserPurchasedBitInfo}
                />
            )}

            {logoutUser && (
                <Logout setLogoutUser={setLogoutUser} setLogedIn={setLogedIn} />
            )}
        </div>
    );
};

export default MainLayout;
